/**
 * Daily Checklist: métricas en filas (agrupadas) × últimos N días en columnas.
 * El backend ya manda cada celda con su `status` (good/warn/bad/null) según el
 * desvío vs el promedio móvil o el objetivo. "Ayer" va remarcado y los días
 * preliminares con asterisco. Primera columna fija, scroll horizontal.
 */
const nf0 = new Intl.NumberFormat('es-UY', { maximumFractionDigits: 0 });
const nf2 = new Intl.NumberFormat('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function fmt(value, unit) {
  if (value === null || value === undefined) return '—';
  switch (unit) {
    case 'money':
      return Math.abs(value) < 100 ? `$${nf2.format(value)}` : `$${nf0.format(value)}`;
    case 'pct':
      return `${nf2.format(value * 100)}%`;
    case 'x':
      return `${nf2.format(value)}x`;
    case 'days':
      return `${nf0.format(value)} d`;
    default:
      return nf0.format(value);
  }
}

const STATUS_CLS = {
  good: 'bg-green-50 text-green-700',
  warn: 'bg-amber-50 text-amber-800',
  bad: 'bg-red-50 text-red-700',
};

const WD = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
function dayLabel(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return { top: WD[dt.getDay()], bottom: `${d}/${m}` };
}

function Legend() {
  const items = [['good', 'bien'], ['warn', 'ojo'], ['bad', 'mal']];
  return (
    <div className="flex items-center gap-3 text-[11px] text-gray-500">
      {items.map(([k, label]) => (
        <span key={k} className="inline-flex items-center gap-1">
          <span className={`inline-block h-2.5 w-2.5 rounded-sm ${STATUS_CLS[k].split(' ')[0]}`} />
          {label}
        </span>
      ))}
    </div>
  );
}

function GroupRows({ group, yesterday }) {
  const span = (group.rows[0]?.cells.length || 0) + 1;
  return (
    <>
      <tr>
        <td
          colSpan={span}
          className="bg-gray-100 px-3 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500"
        >
          {group.title}
        </td>
      </tr>
      {group.rows.map((row) => (
        <tr key={row.key} className="border-t border-gray-100">
          <th className="sticky left-0 z-10 min-w-[200px] bg-white px-3 py-1.5 text-left font-medium text-gray-700">
            {row.label}
          </th>
          {row.cells.map((c) => {
            const isY = c.date === yesterday;
            const cls = STATUS_CLS[c.status] || 'text-gray-700';
            return (
              <td
                key={c.date}
                className={`px-2 py-1.5 text-center tabular-nums ${cls} ${
                  isY ? 'ring-1 ring-inset ring-indigo-300' : ''
                } ${c.preliminary ? 'opacity-70' : ''}`}
              >
                {fmt(c.value, row.unit)}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

export default function DailyChecklistSection({ data, isLoading, isError }) {
  if (isLoading && !data) {
    return <p className="py-12 text-center text-sm text-gray-500">Cargando checklist…</p>;
  }
  if (isError || !data) {
    return (
      <p className="py-12 text-center text-sm text-red-600">
        No se pudo cargar el checklist diario. Probá de nuevo en unos segundos.
      </p>
    );
  }

  const { days = [], yesterday, preliminary = [], groups = [], currency } = data;
  const prelimSet = new Set(preliminary);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-500">
          Últimos {days.length} días · {currency || 'moneda nativa'} · ayer remarcado ·{' '}
          <span title="dato preliminar">* preliminar</span>
        </p>
        <Legend />
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 min-w-[200px] bg-gray-50 px-3 py-2 text-left font-semibold text-gray-600">
                Métrica
              </th>
              {days.map((iso) => {
                const { top, bottom } = dayLabel(iso);
                const isY = iso === yesterday;
                const isP = prelimSet.has(iso);
                return (
                  <th
                    key={iso}
                    className={`px-2 py-1 text-center font-medium tabular-nums ${
                      isY ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-50 text-gray-500'
                    }`}
                  >
                    <div className="leading-tight">{top}</div>
                    <div className="leading-tight">
                      {bottom}
                      {isP && <span title="dato preliminar">*</span>}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <GroupRows key={g.title} group={g} yesterday={yesterday} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
